




















































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { mapState, mapGetters } from 'vuex';

import { Discount } from 'frontend/types/premium';
import {
  Coupon,
  User,
  MobileGetter,
  ExperimentGetter,
  Optional,
  unreachableCase,
  FeatureSwitches,
} from 'frontend/types';
import { SponsorLandingPagesTreatment } from 'frontend/types/landing/marketing';
import { justSignedUpFromMarketingLandingPage } from 'frontend/utils/marketing';
import {
  isLegacyDirectTraffic,
  isLegacySeasonal,
  isStudentDiscount,
} from 'frontend/utils/discounts';
import { addToCartTrack, track } from 'frontend/utils/analytics';
import { embeddedChargebeeCheckoutExperiment } from 'frontend/experiments/embedded_checkout';

import LightningSvg from 'frontend/views/premium/paywall/svgs/LightningSvg.vue';

interface ExperimentContent {
  buttonText: string;
  headline: string;
}

@Component({
  components: {
    LightningSvg,
  },
  computed: {
    ...mapGetters('categorizedCourses', ['roughNumCourses']),
    ...mapState('premiumPurchaseInfo', ['is_discounted']),
    ...mapGetters('banner', ['startTrialCtaClickedAt']),
    ...mapGetters('pageHeader', ['isMobile', 'coupon']),
    ...mapGetters('globals', [
      'inExperiment',
      'sponsorLandingPagesTreatment',
      'discount',
      'featureSwitches',
    ]),
    ...mapState('globals', ['user']),
  },
})
export default class SubscriptionStartButton extends Vue {
  @Prop(Object) private readonly annualPlan!: any;
  @Prop(Boolean) private readonly nux!: boolean | undefined;
  @Prop(Boolean) private readonly isFooter!: boolean | undefined;
  @Prop(Boolean) private readonly matureUsers!: boolean | undefined;
  @Prop(Number) private readonly trialLength!: number;
  @Prop({ type: Boolean, default: false }) private readonly showMoneyBackTextCopy!: boolean;

  private readonly sponsorLandingPagesTreatment!: SponsorLandingPagesTreatment;

  private coupon!: Coupon;
  private user!: User;
  private isMobile!: MobileGetter;
  private inExperiment!: ExperimentGetter;
  private featureSwitches!: FeatureSwitches;
  private startTrialCtaClickedAt!: number;

  // tslint:disable-next-line:variable-name
  private is_discounted!: boolean;
  private readonly discount!: Optional<Discount>;

  private get directTrafficDiscount(): boolean {
    return isLegacyDirectTraffic(this.discount);
  }

  private get seasonalDiscount(): boolean {
    return isLegacySeasonal(this.discount);
  }

  private get isStudentDiscount(): boolean {
    return isStudentDiscount(this.discount);
  }

  private get experimentAwareTrialPlanPriceMessage(): string {
    const asterisk = this.annualPlanIncludesReferralDiscount ? '*' : '';
    const trialBlurb = this.annualPlanIsTrialPlan ? 'after free trial ' : '';
    return `${this.annualPlan.display_price}${asterisk}/month ${trialBlurb}(billed annually)`;
  }

  private get experimentContent(): Optional<ExperimentContent> {
    if (!this.nux || !justSignedUpFromMarketingLandingPage(this.$route)) {
      return undefined;
    }
    if (!this.coupon) {
      return undefined;
    }
    switch (this.sponsorLandingPagesTreatment) {
      case 'control':
        // Use the default text for control
        return undefined;
      case 'regular_nux_updated_paywall':
      case 'short_paywall':
      case 'long_paywall':
        return {
          buttonText: `Try free for ${this.annualPlan.trial_length_in_days} days`,
          headline: `Get ${this.coupon.percent_off}% off Brilliant Premium`,
        };
      default:
        unreachableCase(this.sponsorLandingPagesTreatment);
    }
  }

  private get inEmbeddedCheckoutExperiment(): boolean {
    return (
      embeddedChargebeeCheckoutExperiment(this.inExperiment) &&
      (Boolean(this.featureSwitches.enable_embedded_checkout) ||
        Boolean(this.featureSwitches.enable_embedded_checkout_v2))
    );
  }

  @Watch('startTrialCtaClickedAt')
  private handleAnnualClick() {
    if (!this.user.is_authenticated) {
      this.$store.dispatch('signupModal/signup');
    } else if (this.inEmbeddedCheckoutExperiment) {
      addToCartTrack();
      this.$emit('show-embedded-checkout');
    } else {
      addToCartTrack();
      window.location = this.annualPlan.checkout_url;
    }

    if (this.isFooter) {
      track('ui', {
        element_id: 'clicked_nux_paywall_subscribe_free_trial_annual_footer',
      });
    } else {
      track('ui', {
        axId:
          this.experimentContent !== undefined
            ? 'clicked_continue_sponsor_paywall'
            : 'clicked_nux_paywall_subscribe_free_trial_annual',
      });
    }
  }

  private handleSubscribeNowClick(checkoutUrl: Location) {
    if (!this.user.is_authenticated) {
      this.$store.dispatch('signupModal/signup');
    } else {
      window.location = checkoutUrl;
    }
  }

  private get strikeThroughPrice(): string | null {
    if (
      !this.directTrafficDiscount ||
      this.discount?.is_visible ||
      this.annualPlanIncludesReferralDiscount
    ) {
      // fallback to usual behavior
      if (this.annualPlan.strikethrough_price) {
        return this.annualPlan.strikethrough_price;
      }
    }
    return null;
  }

  private get shouldShowDiscount(): boolean {
    // Referral discount takes precedence
    if (this.annualPlanIncludesReferralDiscount) {
      return true;
    }
    if (this.seasonalDiscount) {
      return true;
    }
    if (this.directTrafficDiscount) {
      return !!this.discount?.is_visible;
    }
    if (this.isStudentDiscount) {
      return true;
    }
    return !!(this.is_discounted || (this.annualPlan && this.annualPlan.strikethrough_price));
  }

  private get campaignDiscountOverrideHtml(): string | null {
    if (this.seasonalDiscount || this.isStudentDiscount) {
      return `${this.discount?.name} discount applied`;
    }
    if (this.directTrafficDiscount) {
      return 'Limited time discount applied';
    }
    return null;
  }

  private get discountHtml() {
    if (this.annualPlanIncludesReferralDiscount) {
      return 'Referral discount unlocked!';
    }
    if (this.campaignDiscountOverrideHtml) {
      return this.campaignDiscountOverrideHtml;
    }
    if (this.coupon && this.coupon.code_used_name) {
      return `Discount unlocked for <em>${this.coupon.code_used_name}</em>`;
    }
    if (this.user.days_since_signup !== null && this.user.days_since_signup <= 7) {
      return 'Discount unlocked! Thanks for joining Brilliant.';
    }
    return 'Discount unlocked! Thanks for being active on Brilliant.';
  }

  private get annualPlanIncludesReferralDiscount(): boolean {
    return this.annualPlan && this.annualPlan.includes_referral_discount;
  }

  private get annualPlanIsTrialPlan(): boolean {
    return this.annualPlan && (this.annualPlan.is_trial || this.annualPlan.is_trial_plan);
  }
}
